import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';

import { type RenderFlyoutOptions } from 'common/visualizations/views/FlyoutRenderer';

import { flyoutRenderer } from 'lib/components/FlyoutRenderer';
import StoryVisualization from 'lib/components/StoryVisualization';
import Constants from 'lib/Constants';
import StorytellerUtils from 'lib/StorytellerUtils';
import { StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import type { FeatureMapBlockComponent, MapBlockComponent, RegionMapBlockComponent } from 'types';

import { type ReactRendererProps } from '../shared/ComponentRegistry';

type MapBlockComponentUnion = MapBlockComponent | FeatureMapBlockComponent | RegionMapBlockComponent;

export const resizableProps = () => {
  return {
    resizeSupported: true,
    resizeOptions: {
      minHeight: Constants.MINIMUM_COMPONENT_HEIGHTS_PX.VISUALIZATION,
    },
  };
};

const mapVisualizationOptions = { displayFilterBar: true, toggleMapLayersInternally: true };

/**
 * React renderer for the map types (map, feature map, region map). Rendering is delegated
 * to StoryVisualization.
 */
export const ComponentMap = ({ componentData }: ReactRendererProps) => {
  const { type, value } = componentData as MapBlockComponentUnion;
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(type);
  const vif = value?.vif;
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = componentRef.current;
    if (!element) {
      return;
    }

    const handleFlyout = (event: Event) => {
      const payload = (event as CustomEvent<RenderFlyoutOptions | null>).detail;
      if (payload !== null) {
        flyoutRenderer.render(payload);
      } else {
        flyoutRenderer.clear();
      }
    };

    element.addEventListener('SOCRATA_VISUALIZATION_FLYOUT', handleFlyout);

    return () => element.removeEventListener('SOCRATA_VISUALIZATION_FLYOUT', handleFlyout);
  }, [vif]);

  if (!vif) {
    return null;
  }

  return (
    <div className={`component ${typeClasses}`} ref={componentRef} data-testid="component-map">
      <div className="component-content story-viz-react">
        <Provider store={StorytellerReduxStoreFactory()}>
          <StoryVisualization storyVif={vif} options={mapVisualizationOptions} />
        </Provider>
      </div>
    </div>
  );
};
